import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Phone, MessageSquare, Clock, User, 
  Check, Reply, Trash2, Eye, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';

const MessagesPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, call_request, message
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAllMessages();
      setMessages(data);
    } catch (error) {
      toast.error('Erreur de chargement des messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleMarkRead = async (messageId) => {
    try {
      await api.markMessageRead(messageId);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, status: 'read' } : m
      ));
      toast.success('Message marqué comme lu');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    try {
      setReplying(true);
      await api.replyToMessage(selectedMessage.id, replyText);
      setMessages(messages.map(m => 
        m.id === selectedMessage.id 
          ? { ...m, status: 'replied', reply: replyText, repliedAt: new Date().toISOString() } 
          : m
      ));
      toast.success('Réponse envoyée');
      setReplyText('');
      setSelectedMessage(null);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setReplying(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    
    try {
      await api.deleteMessage(messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      toast.success('Message supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'unread') return m.status === 'unread';
    return m.type === filter;
  });

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'unread':
        return <Badge className="bg-red-100 text-red-800">Non lu</Badge>;
      case 'read':
        return <Badge className="bg-yellow-100 text-yellow-800">Lu</Badge>;
      case 'replied':
        return <Badge className="bg-green-100 text-green-800">Répondu</Badge>;
      default:
        return null;
    }
  };

  const getTypeBadge = (type) => {
    return type === 'call_request' 
      ? <Badge className="bg-purple-100 text-purple-800"><Phone size={12} className="mr-1" />Demande d&apos;appel</Badge>
      : <Badge className="bg-green-100 text-green-800"><MessageSquare size={12} className="mr-1" />Message</Badge>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center"
                data-testid="back-button"
              >
                <ArrowLeft size={18} className="mr-2" />
                Retour
              </Button>
              <div className="border-l pl-4">
                <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="text-green-600" />
                  Messages & Demandes
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
                  )}
                </h1>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchMessages}
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className="mr-2" />
              Actualiser
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
          >
            Tous ({messages.length})
          </Button>
          <Button 
            variant={filter === 'unread' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('unread')}
          >
            Non lus ({unreadCount})
          </Button>
          <Button 
            variant={filter === 'message' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('message')}
          >
            <MessageSquare size={14} className="mr-1" />
            Messages
          </Button>
          <Button 
            variant={filter === 'call_request' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('call_request')}
          >
            <Phone size={14} className="mr-1" />
            Demandes d&apos;appel
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              </div>
            ) : filteredMessages.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Mail className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-gray-500">Aucun message</p>
                </CardContent>
              </Card>
            ) : (
              filteredMessages.map((message) => (
                <Card 
                  key={message.id} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedMessage?.id === message.id ? 'ring-2 ring-green-500' : ''
                  } ${message.status === 'unread' ? 'border-l-4 border-l-red-500' : ''}`}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (message.status === 'unread') {
                      handleMarkRead(message.id);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <User className="text-gray-400" size={16} />
                        <span className="font-medium">{message.clientName}</span>
                      </div>
                      <div className="flex gap-1">
                        {getTypeBadge(message.type)}
                        {getStatusBadge(message.status)}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{message.clientEmail}</p>
                    {message.subject && (
                      <p className="font-medium text-sm mb-1">{message.subject}</p>
                    )}
                    <p className="text-sm text-gray-700 line-clamp-2">{message.content}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock size={12} />
                        {message.createdAt}
                      </span>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="p-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(message.id);
                          }}
                        >
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Message Detail & Reply */}
          <div className="lg:sticky lg:top-24">
            {selectedMessage ? (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {selectedMessage.type === 'call_request' ? (
                          <Phone className="text-purple-600" />
                        ) : (
                          <MessageSquare className="text-green-600" />
                        )}
                        {selectedMessage.type === 'call_request' ? 'Demande d\'appel' : 'Message'}
                      </CardTitle>
                      <CardDescription>
                        De: {selectedMessage.clientName} ({selectedMessage.clientEmail})
                      </CardDescription>
                    </div>
                    {getStatusBadge(selectedMessage.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedMessage.subject && (
                    <div>
                      <p className="text-sm text-gray-500">Sujet</p>
                      <p className="font-medium">{selectedMessage.subject}</p>
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-gray-500">Message</p>
                    <div className="bg-gray-50 rounded-lg p-4 mt-1">
                      <p className="whitespace-pre-wrap">{selectedMessage.content}</p>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500">
                    Reçu le {selectedMessage.createdAt}
                  </div>

                  {selectedMessage.reply && (
                    <div className="border-t pt-4">
                      <p className="text-sm text-gray-500 mb-2">Votre réponse</p>
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="whitespace-pre-wrap">{selectedMessage.reply}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Répondu le {selectedMessage.repliedAt}
                      </p>
                    </div>
                  )}

                  {selectedMessage.status !== 'replied' && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-2">Répondre</p>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Tapez votre réponse..."
                        className="w-full border rounded-lg p-3 min-h-[100px] resize-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        data-testid="reply-textarea"
                      />
                      <div className="flex gap-2 mt-2">
                        <Button 
                          onClick={handleReply}
                          disabled={replying || !replyText.trim()}
                          className="flex-1"
                          data-testid="send-reply-btn"
                        >
                          <Reply size={16} className="mr-2" />
                          {replying ? 'Envoi...' : 'Envoyer la réponse'}
                        </Button>
                        {selectedMessage.type === 'call_request' && (
                          <Button 
                            variant="outline"
                            onClick={() => window.open(`tel:${selectedMessage.clientPhone || ''}`)}
                          >
                            <Phone size={16} className="mr-2" />
                            Appeler
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Eye className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-gray-500">Sélectionnez un message pour voir les détails</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
